import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import {
  pmsCounterSourceField,
  pmsCounterTypeField,
  pmsEquipmentForVesselField,
  pmsVesselField} from '../shared/pmsFormFields';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsMaintenanceCounter } from './useData';

export const moduleConfig = {
  module: {
    title: "Maintenance counters",
    permissionModuleKey: 'pms_maintenance_counters',
    route: {
      list: '/pms/pms-maintenance-counters',
      new: '/pms/pms-maintenance-counters/new',
      detail: (id: number) => `/pms/pms-maintenance-counters/${id}`,
      edit: (id: number) => `/pms/pms-maintenance-counters/${id}/edit`},
    api: { list: '/pms-maintenance-counters', detail: (id: number) => `/pms-maintenance-counters/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsMaintenanceCounter[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-maintenance-counters/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsMaintenanceCounter) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsMaintenanceCounter } = await import('../../../services/mutations');
      await deletePmsMaintenanceCounter(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Maintenance counters',
    getTitle: (item: PmsMaintenanceCounter) =>
      (item.name as string) || (item.id != null ? `Counter #${item.id}` : 'Counter')},
  form: {
    new: { title: 'New maintenance counter', submitText: 'Create' },
    edit: { title: 'Edit maintenance counter', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-4xl',
    create: async (data: Partial<PmsMaintenanceCounter>) => {
      const { createPmsMaintenanceCounter } = await import('../../../services/mutations');
      const {
        vessel,
        equipment,
        counterType,
        source,
        history,
        schedules,
        id,
        lastRecordedAt,
        createdAt,
        updatedAt,
        createdByUserId,
        updatedByUserId,
        ...payload
      } = data as Record<string, unknown>;
      return createPmsMaintenanceCounter(payload);
    },
    update: async (id: number, data: Partial<PmsMaintenanceCounter>) => {
      const { updatePmsMaintenanceCounter } = await import('../../../services/mutations');
      const {
        vessel,
        equipment,
        counterType,
        source,
        history,
        schedules,
        id: _id,
        lastRecordedAt,
        createdAt,
        updatedAt,
        createdByUserId,
        updatedByUserId,
        ...payload
      } = data as Record<string, unknown>;
      return updatePmsMaintenanceCounter(id, payload);
    }},
  fields: [
    {
      ...pmsVesselField(true),
      helperText: 'Counters are unique by name within a vessel',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('equipmentId', '');
      }},
    {
      ...pmsEquipmentForVesselField(false),
      helperText: 'Optional. Link to a specific equipment slot (e.g. main engine RH)',
      placeholder: 'None — vessel-level counter'},
    {
      ...pmsCounterTypeField(true),
      helperText: 'e.g. Running hours, Starts, Cycles'},
    {
      ...pmsCounterSourceField('sourceId', 'Default source', true),
      helperText: 'Where readings normally come from (manual, integration, …)'},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Main engine running hours',
      validation: { required: true },
      helperText: 'Shown on jobs that depend on this counter'},
    {
      name: 'currentValue',
      label: 'Current value',
      type: 'number',
      min: 0,
      step: 0.01,
      placeholder: '0',
      helperText: 'Latest reading used for due calculations'},
    {
      name: 'unit',
      label: 'Unit',
      type: 'text',
      required: true,
      placeholder: 'e.g. RH, starts, cycles',
      validation: { required: true }},
    {
      name: 'isAutomatic',
      label: 'Automatic',
      type: 'switch',
      switchLabel: 'Updated automatically from integrations'},
    {
      name: 'isActive',
      label: 'Status',
      type: 'switch',
      switchLabel: 'Counter is active'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'scope',
        title: 'Scope',
        description: 'Which vessel (and optional equipment) this counter belongs to.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'equipmentId'],
        fieldSpans: {
          vesselId: 'md:col-span-6',
          equipmentId: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'identity',
        title: 'Counter',
        description: 'Name, type, and how readings are sourced.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['name', 'counterTypeId', 'sourceId', 'currentValue', 'unit'],
        fieldSpans: {
          name: 'md:col-span-12',
          counterTypeId: 'md:col-span-6',
          sourceId: 'md:col-span-6',
          currentValue: 'md:col-span-6',
          unit: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'status',
        title: 'Status',
        description: 'Whether the counter is live and how it is updated.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isAutomatic', 'isActive'],
        fieldSpans: {
          isAutomatic: 'md:col-span-6',
          isActive: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const relName = (
  rel: { code?: string | null; name?: string | null; vesselName?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  if (rel?.vesselName) return rel.vesselName;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelVessel = (item: PmsMaintenanceCounter) => {
  const v = item.vessel as { vesselName?: string; name?: string } | null | undefined;
  if (!v && item.vesselId == null) return '-';
  return v?.vesselName ?? v?.name ?? (item.vesselId != null ? `#${item.vesselId}` : '-');
};

const labelEquipment = (item: PmsMaintenanceCounter) => {
  const e = item.equipment as { name?: string } | null | undefined;
  return e?.name ?? (item.equipmentId != null ? `#${item.equipmentId}` : '-');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsMaintenanceCounter>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'currentValue',
    header: 'Value',
    sortable: true,
    render: (v, row) => (
      <TextCell
        value={
          v != null
            ? `${v}${row.unit != null && row.unit !== '' ? ` ${String(row.unit)}` : ''}`
            : undefined
        }
      />
    )},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsMaintenanceCounter, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      { label: 'Equipment', value: labelEquipment(item) },
      {
        label: 'Counter type',
        value: relName(
          item.counterType as { code?: string; name?: string } | null,
          item.counterTypeId as number | null,
        )},
      {
        label: 'Source',
        value: relName(item.source as { code?: string; name?: string } | null, item.sourceId as number | null)},
      { label: 'Name', value: item.name != null ? String(item.name) : '-' },
      {
        label: 'Current value',
        value:
          item.currentValue != null
            ? `${item.currentValue}${item.unit ? ` ${String(item.unit)}` : ''}`
            : '-'},
      { label: 'Unit', value: item.unit != null ? String(item.unit) : '-' },
      { label: 'Automatic', value: item.isAutomatic ? 'Yes' : 'No' },
      { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
      {
        label: 'Last recorded',
        value: item.lastRecordedAt ? formatDate(String(item.lastRecordedAt)) : '-'},
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsMaintenanceCounter> => ({
  vesselId: undefined,
  equipmentId: undefined,
  counterTypeId: undefined,
  sourceId: undefined,
  name: '',
  currentValue: 0,
  unit: '',
  isAutomatic: false,
  isActive: true});
export const mapItemToFormData = (item: PmsMaintenanceCounter): Partial<PmsMaintenanceCounter> => ({
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  equipmentId: item.equipmentId != null ? Number(item.equipmentId) : undefined,
  counterTypeId: item.counterTypeId != null ? Number(item.counterTypeId) : undefined,
  sourceId: item.sourceId != null ? Number(item.sourceId) : undefined,
  vessel: item.vessel ?? undefined,
  equipment: item.equipment ?? undefined,
  counterType: item.counterType ?? undefined,
  source: item.source ?? undefined,
  name: item.name != null ? String(item.name) : '',
  currentValue: item.currentValue != null ? Number(item.currentValue) : 0,
  unit: item.unit != null ? String(item.unit) : '',
  isAutomatic: item.isAutomatic ?? false,
  isActive: item.isActive ?? true});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
